// "Register on someone's behalf" — the same fields the public landing form
// collects, plus a training picker, for the many callers who phone in because
// they can't complete the online form themselves.
//
// Open to every signed-in role. It is the *only* page a community manager can
// reach; the real enforcement lives in `pipelineProcedure` (src/integrations/
// trpc/init.ts), this route just keeps them from bumping into dead ends.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	redirect,
	useRouteContext,
} from "@tanstack/react-router";
import { Check, Loader2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { JOB_STATUSES, jobStatusesFor, WILAYAS } from "#/lib/algeria";
import { canRegisterLeads, homeRouteFor } from "#/lib/roles";
import { getSession } from "#/lib/session";

export const Route = createFileRoute("/admin/register-lead")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canRegisterLeads(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: RegisterLeadPage,
});

const PHONE_PATTERN = /^(0|\+213)[0-9\s]{8,13}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD =
	"w-full rounded-xl border-0 bg-canvas px-4 py-3 text-sm text-ink outline-none ring-1 ring-line transition-shadow placeholder:text-muted/60 focus:ring-2 focus:ring-teal";

type Form = {
	trainingId: string;
	fullName: string;
	phone: string;
	email: string;
	wilaya: string;
	jobStatus: string;
	notes: string;
};

const EMPTY: Form = {
	trainingId: "",
	fullName: "",
	phone: "",
	email: "",
	wilaya: "",
	jobStatus: "",
	notes: "",
};

function RegisterLeadPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const queryClient = useQueryClient();

	const [form, setForm] = useState<Form>(EMPTY);
	const [error, setError] = useState<string | null>(null);
	const [lastRegistered, setLastRegistered] = useState<string | null>(null);

	const trainingsQuery = useQuery(trpc.trainings.list.queryOptions());
	const trainings = trainingsQuery.data ?? [];
	// The same statuses the chosen training's own form offers — a training with
	// its own list (see jobStatusesFor) must read the same here as out there.
	const jobStatuses =
		jobStatusesFor(trainings.find((t) => t.id === form.trainingId)?.slug)?.map(
			(option) => option.value,
		) ?? JOB_STATUSES;

	// Default to the same training the public site sends people to, so the
	// common case is one less decision.
	useEffect(() => {
		if (form.trainingId || trainings.length === 0) return;
		const fallback = trainings.find((t) => t.isDefault) ?? trainings[0];
		if (fallback) setForm((f) => ({ ...f, trainingId: fallback.id }));
	}, [trainings, form.trainingId]);

	const create = useMutation(
		trpc.leads.createInternal.mutationOptions({
			onSuccess: (lead) => {
				setLastRegistered(lead.fullName);
				// Keep the training selected — a community manager on the phone
				// usually registers several people for the same session in a row.
				setForm((f) => ({ ...EMPTY, trainingId: f.trainingId }));
				setError(null);
				void queryClient.invalidateQueries({
					queryKey: trpc.leads.list.queryKey(),
				});
				// A freshly registered lead lands as NEW — bump the switcher badge.
				void queryClient.invalidateQueries({
					queryKey: trpc.leads.newByTraining.queryKey(),
				});
			},
		}),
	);

	function patch(key: keyof Form, value: string) {
		setForm((f) => ({ ...f, [key]: value }));
	}

	function handleSubmit(event: React.FormEvent) {
		event.preventDefault();
		setError(null);
		setLastRegistered(null);

		if (!form.trainingId) return setError("يرجى اختيار الدورة");
		if (form.fullName.trim().length < 2)
			return setError("يرجى إدخال الاسم الكامل");
		if (!PHONE_PATTERN.test(form.phone.trim()))
			return setError("يرجى إدخال رقم هاتف جزائري صالح");
		if (form.email.trim() && !EMAIL_PATTERN.test(form.email.trim()))
			return setError("البريد الإلكتروني غير صالح");

		create.mutate({
			trainingId: form.trainingId,
			fullName: form.fullName.trim(),
			phone: form.phone.trim(),
			// Email and wilaya are required on the public form but optional here:
			// on a phone call you often only get a name and a number.
			email: form.email.trim() || undefined,
			wilaya: form.wilaya || undefined,
			jobStatus: form.jobStatus || undefined,
			notes: form.notes.trim() || undefined,
		});
	}

	return (
		<div className="mx-auto max-w-2xl" dir="rtl">
			<header className="mb-6">
				<h1 className="font-display text-2xl font-bold text-navy">
					تسجيل طلب نيابة عن زبون
				</h1>
				<p className="mt-1 text-sm text-muted">
					للزبائن الذين يتّصلون بك ولا يستطيعون التسجيل بأنفسهم عبر الموقع. يُسجَّل
					الطلب باسمك في السجل.
				</p>
			</header>

			{lastRegistered ? (
				<div className="mb-5 flex items-center gap-3 rounded-2xl bg-teal-50 px-4 py-3 ring-1 ring-teal/20">
					<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal text-white">
						<Check className="h-4 w-4" />
					</span>
					<p className="text-sm font-semibold text-navy">
						تم تسجيل «{lastRegistered}» بنجاح. يمكنك تسجيل زبون آخر.
					</p>
				</div>
			) : null}

			<form
				onSubmit={handleSubmit}
				noValidate
				className="flex flex-col gap-4 rounded-3xl bg-white p-6 shadow-card"
			>
				<Field label="الدورة" htmlFor="trainingId" required>
					<select
						id="trainingId"
						value={form.trainingId}
						onChange={(e) => patch("trainingId", e.target.value)}
						className={FIELD}
						disabled={trainingsQuery.isLoading}
					>
						<option value="" disabled>
							{trainingsQuery.isLoading ? "جارٍ التحميل…" : "اختر الدورة"}
						</option>
						{trainings.map((t) => (
							<option key={t.id} value={t.id}>
								{t.name}
								{t.isActive ? "" : " (غير نشطة)"}
							</option>
						))}
					</select>
				</Field>

				<div className="grid gap-4 sm:grid-cols-2">
					<Field label="الاسم الكامل" htmlFor="fullName" required>
						<input
							id="fullName"
							type="text"
							autoComplete="off"
							value={form.fullName}
							onChange={(e) => patch("fullName", e.target.value)}
							placeholder="الاسم واللقب"
							className={FIELD}
						/>
					</Field>
					<Field label="رقم الهاتف" htmlFor="phone" required>
						<input
							id="phone"
							type="tel"
							inputMode="tel"
							dir="ltr"
							value={form.phone}
							onChange={(e) => patch("phone", e.target.value)}
							placeholder="0555 00 00 00"
							className={`${FIELD} text-end`}
						/>
					</Field>
				</div>

				<Field label="البريد الإلكتروني (اختياري)" htmlFor="email">
					<input
						id="email"
						type="email"
						inputMode="email"
						dir="ltr"
						value={form.email}
						onChange={(e) => patch("email", e.target.value)}
						placeholder="you@email.com"
						className={`${FIELD} text-end`}
					/>
				</Field>

				<div className="grid gap-4 sm:grid-cols-2">
					<Field label="الولاية (اختياري)" htmlFor="wilaya">
						<select
							id="wilaya"
							value={form.wilaya}
							onChange={(e) => patch("wilaya", e.target.value)}
							className={FIELD}
						>
							<option value="">غير محدَّدة</option>
							{WILAYAS.map((w, i) => (
								<option key={w} value={w}>
									{i + 1} · {w}
								</option>
							))}
						</select>
					</Field>
					<Field label="الوضعية (اختياري)" htmlFor="jobStatus">
						<select
							id="jobStatus"
							value={form.jobStatus}
							onChange={(e) => patch("jobStatus", e.target.value)}
							className={FIELD}
						>
							<option value="">غير محدَّدة</option>
							{jobStatuses.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</Field>
				</div>

				<Field label="ملاحظة (اختيارية)" htmlFor="notes">
					<textarea
						id="notes"
						rows={3}
						maxLength={2000}
						value={form.notes}
						onChange={(e) => patch("notes", e.target.value)}
						placeholder="سبب الاتصال، أو ما طلبه الزبون…"
						className={`${FIELD} resize-none`}
					/>
				</Field>

				{error || create.isError ? (
					<p className="text-sm font-semibold text-rose-600">
						{error ?? "تعذّر تسجيل الطلب، حاول مجدداً."}
					</p>
				) : null}

				<button
					type="submit"
					disabled={create.isPending}
					className="inline-flex items-center justify-center gap-2 rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:bg-teal-600 active:translate-y-0 disabled:pointer-events-none disabled:opacity-60"
				>
					{create.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<>
							<UserPlus className="h-4 w-4" />
							تسجيل الطلب
						</>
					)}
				</button>
			</form>
		</div>
	);
}

function Field({
	label,
	htmlFor,
	required,
	children,
}: {
	label: string;
	htmlFor: string;
	required?: boolean;
	children: React.ReactNode;
}) {
	return (
		<div>
			<label
				htmlFor={htmlFor}
				className="mb-1.5 block text-xs font-bold text-muted"
			>
				{label}
				{required ? <span className="text-rose-500"> *</span> : null}
			</label>
			{children}
		</div>
	);
}
